import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { broadcastScheduleQueryKeys } from '@/features/broadcast-schedule/api/query-keys';
import {
  TBroadcastActiveIds,
  TBroadcastActiveOptions,
} from '@/features/mentions/components/MentionFilters/active-options/active-options';
import { TSegment } from '@/features/segments/types';
import {
  ProgramBroadcastFiltersState,
  useProgramBroadcastFilters,
} from '@/stores/program-broadcast-filters';

import { getBroadcastScheduleSegmentById } from '../api/get-broadcast-schedule-by-id-segment';
import { useBroadcastScheduleProgram } from '../context/BroadcastScheduleProgramContext';

const sortedDistinct = (values: Iterable<string>) => [...new Set(values)].sort();

/** The ids a set of segments relates to, through their mentions, logos and people. */
export function collectActiveIds(segments: TSegment[]): TBroadcastActiveIds {
  const workspaces: string[] = [];
  const tags: string[] = [];
  const logos: string[] = [];
  const people: string[] = [];

  segments.forEach(segment => {
    (segment.segment_mentions ?? []).forEach(mention => {
      workspaces.push(...(mention.workspaces ?? []));
      tags.push(...(mention.tags ?? []));
    });
    (segment.segment_logos ?? []).forEach(({ logo_id }) => logo_id && logos.push(logo_id));
    (segment.segment_peoples ?? []).forEach(({ people_id }) => people_id && people.push(people_id));
  });

  return {
    workspaces: sortedDistinct(workspaces),
    tags: sortedDistinct(tags),
    logos: sortedDistinct(logos),
    people: sortedDistinct(people),
  };
}

/** Every segment of either set, the `preferred` version winning when both hold it. */
export function unionById(fetched: TSegment[], preferred: TSegment[]): TSegment[] {
  const byId = new Map(fetched.map(segment => [segment.id, segment]));
  preferred.forEach(segment => byId.set(segment.id, segment));
  return [...byId.values()];
}

/** Whether the filter bar narrows the list, so the visible segments are a subset. */
export const hasBarFilter = (
  filters: Pick<
    ProgramBroadcastFiltersState,
    'search' | 'review_states' | 'time_keeping_states' | 'workspaces' | 'tags' | 'logos' | 'people'
  >
) =>
  Boolean(filters.search) ||
  filters.review_states.length > 0 ||
  filters.time_keeping_states.length > 0 ||
  filters.workspaces.length > 0 ||
  filters.tags.length > 0 ||
  filters.logos.length > 0 ||
  filters.people.length > 0;

/** The segments the view on screen shows, as the reviewer's in-place edits left them. */
export function useVisibleSegments() {
  const { isGlobalView, globalViewSegments, segmentItems } = useBroadcastScheduleProgram();
  return isGlobalView ? globalViewSegments : segmentItems;
}

type UseProgramBroadcastActiveIdsParams = {
  id: string;
  /** Whether a dropdown that reads the ids is open; gates the unfiltered request. */
  enabled: boolean;
  /**
   * Whether the list's answer has landed in the view on screen. False while it loads and
   * during the render in which it arrives, before the page has copied it.
   */
  listIsSettled: boolean;
};

/**
 * The ids present in the broadcast's segments, as the broadcast source of the filter options.
 * With no bar filter, the view on screen holds every segment, so they come from it with no
 * request. Under a filter, they come from one unfiltered request overlaid with the view on
 * screen.
 */
export function useProgramBroadcastActiveIds({
  id,
  enabled,
  listIsSettled,
}: UseProgramBroadcastActiveIdsParams): TBroadcastActiveOptions {
  const filtered = useProgramBroadcastFilters(hasBarFilter);
  const visible = useVisibleSegments();

  const unfiltered = useQuery({
    queryKey: broadcastScheduleQueryKeys.activeOptionSegments(id),
    queryFn: () => getBroadcastScheduleSegmentById({ id }),
    enabled: Boolean(id) && filtered && enabled,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const ids = useMemo(() => {
    if (!id) return undefined;
    if (!filtered) return listIsSettled ? collectActiveIds(visible) : undefined;
    return unfiltered.data ? collectActiveIds(unionById(unfiltered.data, visible)) : undefined;
  }, [id, filtered, listIsSettled, visible, unfiltered.data]);

  const isError = filtered && unfiltered.isError;
  // RISK: loading is "ids not known yet", never `isPending`. A disabled query stays pending
  // forever, and unknown ids read as an empty list would flash "Sin resultados".
  const isFetching = (filtered && unfiltered.isFetching) || (ids === undefined && !isError);

  return useMemo(
    () => ({ source: 'programBroadcast', ids, isFetching, isError }),
    [ids, isFetching, isError]
  );
}
